import React from 'react';
import { useTranslation } from 'react-i18next';
import { JobSearchResult } from '../../types/job';
import { formatDateLocalized } from '../../utils/dateUtils';
import { getJobExpirationInfo } from '../../utils/jobUtils';

import { getFreelancerSubtitle } from '../../utils/freelancerUtils';
import { Button } from '../common/Button';

const JobSearchCard: React.FC<{
  job: JobSearchResult;
  onSelect?: (job: JobSearchResult) => void;
}> = ({ job, onSelect }) => {
  const { t, i18n } = useTranslation(['jobs', 'common']);

  const jobListingTypeLabel =
    job.listing_type_code === 'featured' || job.listing_type_code === 'urgent'
      ? t(`job_listing_types.${job.listing_type_code}`, { ns: 'common' })
      : null;
  const publishedAtLabel =
    job.visibility === 'private'
      ? t('requested_at', { ns: 'jobs' })
      : t('published_at', { ns: 'jobs' });
  const publishedDate =
    job.visibility === 'private'
      ? job.created_at
        ? formatDateLocalized(job.created_at, i18n.language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : null
      : job.published_at
        ? formatDateLocalized(job.published_at, i18n.language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : null;

  const expirationInfo = getJobExpirationInfo(job, t, i18n.language);

  return (
    <div
      role="button"
      className={`bg-white p-2 rounded-lg mx-4 md:mx-0 ${
        job.listing_type_code === 'urgent'
          ? 'border-2 border-warning'
          : job.listing_type_code === 'featured'
            ? 'border-2 border-[#39C463]'
            : 'border-2 border-bg-secondary-light'
      }`}
      onClick={() => onSelect?.(job)}
    >
      <div className="m-4 grid grid-cols-1 md:grid-cols-[40%_1fr_1fr_1fr_auto] gap-3 md:gap-4">
        <div className="flex flex-col md:flex-start">
          <div className="flex flex-col md:flex-col">
            <h2 className="text-xl md:text-2xl font-bold text-secondary">{job.title}</h2>
            <p className="font-normal text-xs md:ml-0 md:line-clamp-2 md:w-full ml-0 w-full">
              {getFreelancerSubtitle({ service_category_slugs: job.service_category_slugs }, t)}
            </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:gap-8 gap-2 justify-end md:justify-end m-0 md:m-0 md:contents">
          <div className="flex flex-row md:flex-col md:items-start items-center justify-between md:col-start-2 self-start">
            <span className="text-xs after:content-[':'] md:after:content-['']">
              {t('form.location', { ns: 'jobs' })}{' '}
            </span>
            <span className="font-bold md:ml-0 text-xs ml-1">{job.city_name}</span>
          </div>

          <div className="flex flex-row md:flex-col md:items-start items-center justify-between md:col-start-3 self-start">
            <span className="text-sm after:content-[':'] md:after:content-['']">
              {t('budget', { ns: 'jobs' })}
            </span>
            <span className="font-bold md:ml-0 text-xs ml-1">
              {t(`budget_ranges.${job.budget_range_code}`, { ns: 'common' })}
            </span>
          </div>

          <div className="flex flex-row md:flex-col items-center md:items-start justify-between gap-0 md:gap-1 md:col-start-4 self-start">
            {publishedDate && (
              <div className="flex md:flex hidden md:flex-col text-secondary">
                <span className="text-sm">{publishedAtLabel}</span>
                <span className="font-bold text-xs">{publishedDate}</span>
              </div>
            )}

            {publishedDate && (
              <div className="flex md:hidden flex-row items-center justify-between w-full text-secondary">
                <span className="text-sm">{publishedAtLabel}</span>
                <span className="font-bold text-xs ml-2">{publishedDate}</span>
              </div>
            )}

            {expirationInfo && (
              <>
                <div className="hidden md:flex md:flex-col text-secondary md:mt-2">
                  <span className="text-sm">{expirationInfo.label}</span>
                  <span className="font-bold text-xs">{expirationInfo.value}</span>
                </div>
                <div className="flex md:hidden flex-row items-center justify-between w-full text-secondary">
                  <span className="text-sm">{expirationInfo.label}</span>
                  <span className="font-bold text-xs ml-2">{expirationInfo.value}</span>
                </div>
              </>
            )}
          </div>

          {jobListingTypeLabel && (
            <div className="flex md:flex-col md:justify-self-end md:self-start md:col-start-5 md:col-end-6">
              <div className="flex md:flex hidden md:flex-col items-center md:items-center md:justify-center md:justify-self-end" />
              <div className="flex md:hidden w-full">
                <span
                  className={`text-white text-xs px-4 h-7 leading-none rounded-full font-bold whitespace-nowrap inline-flex items-center justify-center ${
                    job.listing_type_code === 'urgent' ? 'bg-warning' : 'bg-[#39C463]'
                  }`}
                >
                  {jobListingTypeLabel}
                </span>
              </div>
              <div className="hidden md:flex flex-col items-center">
                <span
                  className={`text-white text-xs px-4 h-7 leading-none rounded-full font-bold whitespace-nowrap inline-flex items-center justify-center ${
                    job.listing_type_code === 'urgent' ? 'bg-warning' : 'bg-[#39C463]'
                  }`}
                >
                  {jobListingTypeLabel}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 m-4">
        <p className="mr-0 md:mr-16 whitespace-normal overflow-visible md:truncate md:line-clamp-2">
          {job.description}
        </p>

        <Button
          onClick={() => onSelect?.(job)}
          variant="transparent-with-border"
          className="inline-flex md:inline-flex w-full md:w-auto justify-center"
        >
          {t('viewProjectInfo', { ns: 'jobs' })}
        </Button>
      </div>
    </div>
  );
};

export default JobSearchCard;
